/**
 * Countdown CTA.
 *
 * Counts down to a fixed UTC moment emitted by PHP and removes itself from the
 * DOM the instant that moment passes, including on boot - which is how a stale
 * full-page-cached copy corrects itself within the first frame.
 *
 * formatCta() and summarise() below mirror pioneer_format_countdown_cta() and
 * pioneer_countdown_cta_summary() in inc/countdown-cta.php, so the display
 * never shifts when JS takes over. Change one, change the other.
 *
 * This is the cta style only. The pill and strip styles have their own scripts
 * in src/js/countdown-pill.js and src/js/countdown-strip.js, and the three are
 * intentionally independent.
 */
(function () {
  'use strict';

  var DAY = 86400;
  var HOUR = 3600;
  var MINUTE = 60;

  // Milliseconds past a boundary to aim for - comfortably clear of timer jitter
  // and of clock coarsening, far too small to see. See schedule().
  var LATCH = 120;

  // The polite summary must not chatter, so it is rewritten at most this often.
  var SUMMARY_MS = 60000;

  function pad(value) {
    return value < 10 ? '0' + value : String(value);
  }

  /**
   * Values for the unit cells. Days, hours and minutes always; seconds only
   * when the field is on.
   */
  function formatCta(remaining, showSeconds) {
    remaining = Math.max(0, remaining);

    var values = {
      days: Math.floor(remaining / DAY),
      hours: Math.floor(remaining / HOUR) % 24,
      minutes: Math.floor(remaining / MINUTE) % 60,
      seconds: remaining % 60
    };

    var keys = ['days', 'hours', 'minutes'];

    if (showSeconds) {
      keys.push('seconds');
    }

    var cells = [];

    for (var i = 0; i < keys.length; i++) {
      cells.push({ key: keys[i], value: pad(values[keys[i]]), one: values[keys[i]] === 1 });
    }

    return cells;
  }

  function plural(value, noun) {
    return value + ' ' + noun + (value === 1 ? '' : 's');
  }

  /**
   * Coarse phrase for the polite summary - never the ticking digits.
   */
  function summarise(remaining) {
    remaining = Math.max(0, remaining);

    var days = Math.floor(remaining / DAY);
    var hours = Math.floor(remaining / HOUR) % 24;
    var minutes = Math.floor(remaining / MINUTE) % 60;
    var amount;

    if (days > 0 && hours > 0) {
      amount = plural(days, 'day') + ' and ' + plural(hours, 'hour');
    } else if (days > 0) {
      amount = plural(days, 'day');
    } else if (hours > 0 && minutes > 0) {
      amount = plural(hours, 'hour') + ' and ' + plural(minutes, 'minute');
    } else if (hours > 0) {
      amount = plural(hours, 'hour');
    } else if (minutes > 0) {
      amount = plural(minutes, 'minute');
    } else {
      amount = 'Less than a minute';
    }

    return amount + ' until the Summit';
  }

  function Cta(root) {
    this.root = root;
    this.target = Date.parse(root.getAttribute('data-countdown-target'));
    this.showSeconds = root.getAttribute('data-countdown-seconds') === '1';
    this.status = root.querySelector('[data-countdown-status]');
    this.values = {};
    this.labels = {};
    this.printed = {};
    this.printedStatus = this.status ? this.status.textContent : '';
    this.statusAt = 0;
    this.timer = null;

    var i;
    var valueNodes = root.querySelectorAll('[data-countdown-value]');

    for (i = 0; i < valueNodes.length; i++) {
      var key = valueNodes[i].getAttribute('data-countdown-value');

      this.values[key] = valueNodes[i];
      // Seed from the server rendered digits so the first tick is a no-op.
      this.printed[key] = valueNodes[i].textContent;
    }

    var labelNodes = root.querySelectorAll('[data-countdown-label]');

    for (i = 0; i < labelNodes.length; i++) {
      this.labels[labelNodes[i].getAttribute('data-countdown-label')] = labelNodes[i];
    }

    this.onVisibility = this.handleVisibility.bind(this);
  }

  Cta.prototype.remainingMs = function () {
    return this.target - Date.now();
  };

  /**
   * Write one cell, and only when what is on screen has actually changed.
   */
  Cta.prototype.print = function (key, digits, one) {
    if (this.printed[key] !== digits) {
      this.printed[key] = digits;

      if (this.values[key]) {
        this.values[key].textContent = digits;
      }
    }

    var label = this.labels[key];

    if (!label) {
      return;
    }

    var text = label.getAttribute(one ? 'data-countdown-singular' : 'data-countdown-plural');

    if (text && label.textContent !== text) {
      label.textContent = text;
    }
  };

  Cta.prototype.update = function () {
    var remainingMs = this.remainingMs();

    if (remainingMs <= 0) {
      this.destroy();

      return;
    }

    // Round up, to match pioneer_countdown_cta_remaining(): PHP truncates the
    // current second when it subtracts timestamps, so the server rounds the
    // remainder up. Flooring here would render a second less than the page was
    // served with - a visible jump the moment JS boots.
    var remaining = Math.ceil(remainingMs / 1000);
    var cells = formatCta(remaining, this.showSeconds);

    for (var i = 0; i < cells.length; i++) {
      this.print(cells[i].key, cells[i].value, cells[i].one);
    }

    var now = Date.now();

    if (this.status && now - this.statusAt >= SUMMARY_MS) {
      this.statusAt = now;

      var summary = summarise(remaining);

      if (summary !== this.printedStatus) {
        this.printedStatus = summary;
        this.status.textContent = summary;
      }
    }
  };

  /**
   * Wake just after the displayed value next changes, so with seconds off
   * nothing runs between minute boundaries. Aligned to the target rather than
   * the wall clock, because that is what the remaining time steps on.
   *
   * Two details keep this honest. The change lands a second below a multiple of
   * the step, because the remainder is rounded up. And LATCH aims a little past
   * that moment: timers can fire a shade early and Date.now() is coarse, so
   * aiming exactly at it lands on the wrong side about half the time, the value
   * has not changed yet, the next delay computes to nearly zero, and the
   * countdown fires twice in quick succession - one value flashing past in a
   * few milliseconds and the next appearing stuck.
   */
  Cta.prototype.schedule = function () {
    var step = this.showSeconds ? 1 : 60;
    var remainingMs = this.remainingMs();
    var seconds = Math.ceil(remainingMs / 1000);
    var nextChange = (Math.floor(seconds / step) * step - 1) * 1000;
    var delay = remainingMs - nextChange + LATCH;

    // Never sleep past the target: the last wake is the one that removes the
    // countdown, and it should land on the moment itself, not a latch later.
    if (delay > remainingMs) {
      delay = remainingMs;
    }

    var self = this;

    this.timer = window.setTimeout(function () {
      self.timer = null;
      self.update();

      if (self.root.isConnected) {
        self.schedule();
      }
    }, delay);
  };

  Cta.prototype.stop = function () {
    if (this.timer !== null) {
      window.clearTimeout(this.timer);
      this.timer = null;
    }
  };

  /**
   * A hidden tab does not need to tick; re-sync from the clock on return rather
   * than resuming where it paused.
   */
  Cta.prototype.handleVisibility = function () {
    this.stop();

    if (document.hidden) {
      return;
    }

    this.update();

    if (this.root.isConnected) {
      this.schedule();
    }
  };

  /**
   * Removing the countdown must not move the button it sits beside, which is
   * why the row it shares is packed from the start rather than centred or
   * spaced apart - see .countdown-cta-row.
   */
  Cta.prototype.destroy = function () {
    this.stop();
    document.removeEventListener('visibilitychange', this.onVisibility);

    if (this.root.parentNode) {
      this.root.parentNode.removeChild(this.root);
    }
  };

  Cta.prototype.init = function () {
    // No usable target, or it has already passed on a cached page.
    if (isNaN(this.target) || this.remainingMs() <= 0) {
      this.destroy();

      return;
    }

    // Correct a stale cached render before the first frame, then settle in.
    this.statusAt = Date.now();
    this.update();

    if (this.root.isConnected) {
      document.addEventListener('visibilitychange', this.onVisibility);
      this.schedule();
    }
  };

  function init() {
    var countdowns = document.querySelectorAll('[data-countdown-cta]');

    for (var i = 0; i < countdowns.length; i++) {
      new Cta(countdowns[i]).init();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
